import React from 'react';
import { TrendingUp } from 'lucide-react';
import { GoldBellMark } from './NoFeeLogo';

/**
 * DashboardPreview — premium black/gold mini-dashboard mockup.
 * Reused across signup pages (`/start`, `/complete-subscription`) to give
 * restaurant owners a tangible preview of what they're activating.
 */
const DashboardPreview = ({ caption = 'Your dashboard — once activated' }) => (
  <div className="space-y-3" aria-hidden="true">
    <div
      className="relative rounded-[20px] border border-white/[0.07] bg-gradient-to-br from-zinc-900 via-black to-zinc-950 p-5 shadow-[0_30px_70px_-25px_rgba(212,175,55,0.18)]"
      data-testid="dashboard-preview"
    >
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <GoldBellMark size={22} />
          <span className="text-[11px] font-black tracking-tight text-white">
            No<span className="text-gold-400">Fee</span>
            <span className="ml-1 text-white/40 text-[9px] font-medium tracking-wide">Dashboard</span>
          </span>
        </div>
        <div className="flex gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400/80" />
          <span className="w-1.5 h-1.5 rounded-full bg-amber-400/80" />
          <span className="w-1.5 h-1.5 rounded-full bg-rose-400/80" />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-4">
        {[
          { label: 'Today', value: '$1,284', sub: '+12%' },
          { label: 'This week', value: '$8,420', sub: '+5%' },
          { label: 'Saved vs DoorDash', value: '$2,109', sub: 'this month' },
        ].map((s, i) => (
          <div key={i} className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-2.5">
            <p className="text-[8.5px] uppercase tracking-[0.15em] text-white/40 font-bold">{s.label}</p>
            <p className="mt-0.5 text-[14px] font-black text-white tabular-nums leading-none">{s.value}</p>
            <p className="mt-1 inline-flex items-center gap-0.5 text-[9px] text-emerald-300 font-semibold">
              <TrendingUp className="w-2.5 h-2.5" />
              {s.sub}
            </p>
          </div>
        ))}
      </div>
      <div className="space-y-1.5">
        {[
          ['#1284', 'Margarita Pizza · 1x', '$24.50', 'paid'],
          ['#1283', 'Tacos al Pastor · 3x', '$36.00', 'preparing'],
          ['#1282', 'Açaí Bowl · 2x', '$18.00', 'paid'],
        ].map(([id, item, amt, status], i) => (
          <div key={i} className="flex items-center justify-between text-[10px] py-2 px-2.5 rounded-lg bg-white/[0.02] border border-white/[0.04]">
            <div className="flex items-center gap-2">
              <span className="font-mono text-white/40">{id}</span>
              <span className="text-white/75 truncate max-w-[110px]">{item}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-white font-bold tabular-nums">{amt}</span>
              <span
                className={`px-1.5 py-0.5 rounded text-[8px] font-bold uppercase tracking-wider ${
                  status === 'paid'
                    ? 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/30'
                    : 'bg-amber-500/10 text-amber-300 border border-amber-500/30'
                }`}
              >
                {status}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
    {caption && (
      <p className="text-[11px] uppercase tracking-[0.18em] text-neutral-500 font-bold text-center">
        {caption}
      </p>
    )}
  </div>
);

export default DashboardPreview;
